(() => {
  if (customElements.get('aware-visible-videos')) return;
  customElements.define('aware-visible-videos', class extends HTMLElement {
    connectedCallback() {
      this.videos = [...this.querySelectorAll('video')];
      if (!this.videos.length || this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.ratios = new Map();
      this.userPaused = new Set();
      this.automaticPauses = new WeakSet();
      this.pending = new Set();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };
      this.videos.forEach(video => {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'metadata';
        video.addEventListener('play', () => {
          this.userPaused.delete(video);
          video.closest('[data-video-card]')?.classList.add('is-playing');
          video.closest('[data-video-card]')?.querySelector('[data-video-toggle]')?.setAttribute('aria-pressed', 'true');
        }, { signal });
        video.addEventListener('pause', () => {
          if (this.automaticPauses.has(video)) this.automaticPauses.delete(video);
          else this.userPaused.add(video);
          video.closest('[data-video-card]')?.classList.remove('is-playing');
          video.closest('[data-video-card]')?.querySelector('[data-video-toggle]')?.setAttribute('aria-pressed', 'false');
        }, { signal });
      });
      this.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        const button = target?.closest('[data-video-toggle]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        event.stopPropagation();
        const video = button.closest('[data-video-card]')?.querySelector('video');
        if (!video) return;
        if (video.paused) {
          this.userPaused.delete(video);
          video.play().catch(() => {});
        } else {
          this.userPaused.add(video);
          video.pause();
        }
      }, { signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => this.ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0));
          this.schedule();
        }, { threshold: [0, .35, .6, 1] });
        this.videos.forEach(video => this.observer.observe(video));
      }
      this.mutations = new MutationObserver(this.schedule);
      this.mutations.observe(this, { subtree: true, attributes: true, attributeFilter: ['open'] });
      document.addEventListener('visibilitychange', this.schedule, { signal });
      window.addEventListener('pageshow', this.schedule, { signal });
      window.addEventListener('pagehide', () => this.videos.forEach(video => this.pause(video)), { signal });
      this.reducedMotion.addEventListener('change', this.schedule, { signal });
    }
    visible(video) {
      const modal = this.querySelector('dialog[open]');
      return !document.hidden && (this.ratios.get(video) ?? 0) >= .35 && (!modal || modal.contains(video));
    }
    pause(video) {
      if (!video.paused) {
        this.automaticPauses.add(video);
        video.pause();
      }
    }
    sync() {
      this.videos.forEach(video => {
        if (!this.visible(video)) {
          this.pause(video);
          return;
        }
        if (this.reducedMotion.matches || this.userPaused.has(video) || !video.paused || this.pending.has(video)) return;
        video.muted = true;
        this.pending.add(video);
        video.play().then(() => {
          if (!this.isConnected || !this.visible(video)) this.pause(video);
        }).catch(() => {
          video.closest('[data-video-card]')?.classList.remove('is-playing');
        }).finally(() => this.pending.delete(video));
      });
    }
    disconnectedCallback() {
      this.videos?.forEach(video => this.pause(video));
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.mutations?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  });
})();
